/**
 * «Mi revisión» (biblioteca.html y ficha.html, sidepane): lo que le toca a
 * quien mira, con datos reales — las preguntas de mis cuadernos (P1), las
 * fichas de la casa que puedo revisar como segundo socio, y los «así no»
 * que esperan al juez (los de mis cuadernos; todos, con el rol completo).
 * Con la tarjeta enfocada, A dispara su acto. Sin conexión se lee pero no
 * se actúa: los actos van apagados con porqué.
 */
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { Biblioteca } from "@skein/contracts";
import { Empty, HitlCard, Kbd, Mono, QLane, Sidepane, SidepaneSection, Skeleton, useToast } from "@skein/ui";
import { useCuadernosDe, type BibliotecaApi } from "../../api/biblioteca";
import { etiquetaArea, primeraFicha, primeraRevision, tarjetas, type Tarjeta } from "./logica";

/** «Abrir» un cuaderno: a su primera ficha con pregunta (o con revisión pendiente). */
export function useAbrirCuaderno(api: BibliotecaApi) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const say = useToast();
  return async (code: string, para: "pregunta" | "revision" = "pregunta") => {
    try {
      const c = await qc.fetchQuery({ queryKey: ["cuaderno", code], queryFn: () => api.cuaderno(code), staleTime: 30_000 });
      const f = para === "revision" ? primeraRevision(c.fichas) : primeraFicha(c.fichas);
      if (f) void navigate({ to: "/conocimiento/$slug", params: { slug: f.slug } });
      else say(`${code} aún no tiene fichas.`);
    } catch {
      say(`No llego al cuaderno ${code}: reintento solo cada 30 s.`);
    }
  };
}

/** `seccion`: va como sección de una columna por secciones (la biblioteca, bajo «Uso en el Despacho»); si no, es la columna entera (la ficha). */
export function MiRevision({ api, biblioteca, sinConexion, cargando, seccion }: { api: BibliotecaApi; biblioteca: Biblioteca | undefined; sinConexion: boolean; cargando?: boolean | undefined; seccion?: boolean | undefined }) {
  const navigate = useNavigate();
  const say = useToast();
  const abrir = useAbrirCuaderno(api);
  const me = biblioteca?.me;
  // los «así no» viven en el detalle de cada cuaderno: los míos, o todos con el rol completo
  const codes = !me ? [] : me.rol === "completo" ? (biblioteca?.cuadernos.map((c) => c.code) ?? []) : me.misCuadernos;
  const detalle = useCuadernosDe(api, codes).flatMap((q) => (q.data ? [q.data] : []));
  const ts = biblioteca ? tarjetas(biblioteca, detalle) : [];
  const p1 = ts.filter((t) => t.k !== "asino");
  const otros = ts.filter((t) => t.k === "asino");
  const apagado = () => say("Sin conexión: ahora no se puede cambiar nada.");

  const card = (t: Tarjeta) => {
    if (t.k === "criterio") {
      const n = t.cuaderno.preguntasPendientes;
      return (
        <HitlCard key={`c-${t.cuaderno.code}`} kind="◈ criterio · dueño" sla={`≈ ${n * 2} min`}
          title={`Contestar ${n} ${n === 1 ? "pregunta" : "preguntas"} de ${t.cuaderno.code} ${t.cuaderno.title}`}
          rows={[{ k: "cómo", v: "de una en una, con la ficha al lado; dos minutos cada una" }, { k: "después", v: "tu respuesta queda como criterio de la casa con tu nombre" }]}
          actions={[{ label: "Empezar", kbd: "A", tone: "ok", stale: sinConexion, onClick: () => (sinConexion ? apagado() : void abrir(t.cuaderno.code)) }]} />
      );
    }
    if (t.k === "revision") {
      const d = t.cuaderno.dueno;
      return (
        <HitlCard key={`r-${t.cuaderno.code}`} kind="◈ revisión · segundo socio" sla={`≈ ${Math.max(5, Math.round((t.casa * 10) / 3))} min`}
          title={`Revisar ${t.casa} ${t.casa === 1 ? "ficha" : "fichas"} de ${t.cuaderno.code} ya de la casa (volcadas)`}
          rows={[{ k: "quién firmó", v: d ? `${d.name}, dueño de ${etiquetaArea(t.cuaderno)}` : "el criterio ya está en git" }, { k: "después", v: "con tu «la doy por buena», la ficha pasa a revisada" }]}
          actions={[{ label: "Abrir", kbd: "A", tone: "ok", stale: sinConexion, onClick: () => (sinConexion ? apagado() : void abrir(t.cuaderno.code, "revision")) }]} />
      );
    }
    return (
      <HitlCard key={`a-${t.slug}`} kind="△ así no · juez" sla="sin plazo"
        title={<>Ficha con «así no» pendiente del juez: <Mono>{t.notebook} · {t.slug}</Mono></>}
        rows={[{ k: "qué pasa", v: me?.rol === "completo" ? "el juez la vuelve a pasar por los críticos o la cierra con una resolución" : "el juez la vuelve a pasar por los críticos y te pedirá la firma otra vez; no tienes que hacer nada" }]}
        actions={[{ label: "ver la ficha", variant: "link", onClick: () => void navigate({ to: "/conocimiento/$slug", params: { slug: t.slug } }) }]} />
    );
  };

  const cuerpo = (
    <>
      {p1.length > 0 && <QLane>lo tuyo</QLane>}
      {p1.map(card)}
      {otros.length > 0 && <QLane p={0}>Esperando a otros</QLane>}
      {otros.map(card)}
      {cargando === true && <Skeleton style={{ height: "9rem" }} />}
      {cargando !== true && ts.length === 0 && <Empty inline title="Nada te espera ahora.">Cuando elijas un área o te toque revisar, aparecerá aquí.</Empty>}
    </>
  );
  const cabecera = { title: "Mi revisión", count: p1.length, hot: p1.length > 0, keys: <span className="keys"><Kbd>A</Kbd></span> };
  return seccion === true
    ? <SidepaneSection {...cabecera}>{cuerpo}</SidepaneSection>
    : <Sidepane className="aside" aria-label="Mi revisión" {...cabecera}>{cuerpo}</Sidepane>;
}
